import type { FlowDefinition, FlowFieldDefinition, FlowNode } from '../types';
import {
  buildViewerControlFields,
  connectFlowSockets,
  getViewerControlActiveCombinationFieldId,
  getViewerControlActiveFinishFieldId,
  getViewerControlActiveProductFieldId,
  getViewerControlClosedFieldId,
  getViewerControlModelLoadedFieldId,
  getViewerControlOpenedFieldId,
  rebuildFlowConnections,
  VASE_FLOW_FIELD_IDS,
} from './flows';
import { resolveOrCreateSectionInsertionFlow } from './sectionInsertionFlow';

export type CustomiserBlockNodeType =
  | 'customiser-viewer'
  | 'customiser-finish-picker'
  | 'customiser-colour-filter'
  | 'customiser-combination-results';

const CUSTOMISER_FLOW_FIELDS: FlowFieldDefinition[] = [
  {
    id: VASE_FLOW_FIELD_IDS.chosenProduct,
    label: 'Chosen Product',
    type: 'entity.product',
  },
  {
    id: VASE_FLOW_FIELD_IDS.chosenFinish,
    label: 'Chosen Finish',
    type: 'scalar.string',
  },
  {
    id: VASE_FLOW_FIELD_IDS.chosenColours,
    label: 'Chosen Colours',
    type: 'list.string',
    multiple: true,
  },
  {
    id: VASE_FLOW_FIELD_IDS.chosenCombination,
    label: 'Chosen Combination',
    type: 'entity.combination',
  },
];

const LEGACY_CUSTOMISER_INPUT_BINDINGS: Partial<
  Record<CustomiserBlockNodeType, Record<string, string>>
> = {
  'customiser-viewer': {
    product: VASE_FLOW_FIELD_IDS.chosenProduct,
    finish: VASE_FLOW_FIELD_IDS.chosenFinish,
    combination: VASE_FLOW_FIELD_IDS.chosenCombination,
  },
  'customiser-finish-picker': {
    product: VASE_FLOW_FIELD_IDS.chosenProduct,
  },
  'customiser-colour-filter': {
    product: VASE_FLOW_FIELD_IDS.chosenProduct,
    finish: VASE_FLOW_FIELD_IDS.chosenFinish,
  },
  'customiser-combination-results': {
    product: VASE_FLOW_FIELD_IDS.chosenProduct,
    finish: VASE_FLOW_FIELD_IDS.chosenFinish,
    colours: VASE_FLOW_FIELD_IDS.chosenColours,
  },
};

const LEGACY_CUSTOMISER_OUTPUT_BINDINGS: Partial<
  Record<CustomiserBlockNodeType, Record<string, string>>
> = {
  'customiser-finish-picker': {
    finish: VASE_FLOW_FIELD_IDS.chosenFinish,
  },
  'customiser-colour-filter': {
    colours: VASE_FLOW_FIELD_IDS.chosenColours,
  },
  'customiser-combination-results': {
    combination: VASE_FLOW_FIELD_IDS.chosenCombination,
  },
};

function upsertFields(fields: FlowFieldDefinition[], additions: FlowFieldDefinition[]) {
  const nextById = new Map(fields.map((field) => [field.id, field]));
  additions.forEach((field) => {
    if (!nextById.has(field.id)) {
      nextById.set(field.id, field);
    }
  });
  return Array.from(nextById.values());
}

function upsertNodes(nodes: FlowNode[], additions: FlowNode[]) {
  const nextByBlockId = new Map(nodes.map((node) => [node.blockId, node]));
  additions.forEach((node) => {
    const existing = nextByBlockId.get(node.blockId);
    if (!existing) {
      nextByBlockId.set(node.blockId, node);
      return;
    }

    const mergeBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      defaults: FlowNode['inputs'] | FlowNode['outputs']
    ) => {
      const next = [...current];
      defaults.forEach((binding) => {
        if (!next.some((entry) => entry.socketId === binding.socketId)) {
          next.push(binding);
        }
      });
      return next;
    };

    const stripLegacyBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      direction: 'input' | 'output'
    ) => {
      const legacyBindings =
        direction === 'input'
          ? LEGACY_CUSTOMISER_INPUT_BINDINGS[node.blockType as CustomiserBlockNodeType]
          : LEGACY_CUSTOMISER_OUTPUT_BINDINGS[node.blockType as CustomiserBlockNodeType];
      if (!legacyBindings) return current;
      return current.filter(
        (binding) => legacyBindings[binding.socketId] !== binding.fieldId
      );
    };

    const replaceOutputBindings = (
      current: FlowNode['outputs'],
      defaults: FlowNode['outputs']
    ) => {
      const defaultSocketIds = new Set(defaults.map((binding) => binding.socketId));
      return [
        ...current.filter((binding) => !defaultSocketIds.has(binding.socketId)),
        ...defaults,
      ];
    };

    nextByBlockId.set(node.blockId, {
      ...existing,
      id: existing.id || node.id,
      blockType: node.blockType,
      config: { ...(node.config ?? {}), ...(existing.config ?? {}) },
      inputs: mergeBindings(stripLegacyBindings(existing.inputs, 'input'), node.inputs),
      outputs: replaceOutputBindings(stripLegacyBindings(existing.outputs, 'output'), node.outputs),
    });
  });
  return Array.from(nextByBlockId.values());
}

function buildCustomiserBlockNode(blockType: CustomiserBlockNodeType, blockId: string): FlowNode {
  if (blockType === 'customiser-viewer') {
    return {
      id: `flow-node-${blockId}`,
      blockId,
      blockType,
      inputs: [],
      outputs: [
        { socketId: 'activeProduct', fieldId: getViewerControlActiveProductFieldId(blockId) },
        { socketId: 'activeFinish', fieldId: getViewerControlActiveFinishFieldId(blockId) },
        {
          socketId: 'activeCombination',
          fieldId: getViewerControlActiveCombinationFieldId(blockId),
        },
        { socketId: 'onViewerOpened', fieldId: getViewerControlOpenedFieldId(blockId) },
        { socketId: 'onViewerClosed', fieldId: getViewerControlClosedFieldId(blockId) },
        { socketId: 'onModelLoaded', fieldId: getViewerControlModelLoadedFieldId(blockId) },
      ],
    };
  }

  if (blockType === 'customiser-finish-picker') {
    return {
      id: `flow-node-${blockId}`,
      blockId,
      blockType,
      inputs: [],
      outputs: [],
    };
  }

  if (blockType === 'customiser-colour-filter') {
    return {
      id: `flow-node-${blockId}`,
      blockId,
      blockType,
      inputs: [],
      outputs: [],
    };
  }

  return {
    id: `flow-node-${blockId}`,
    blockId,
    blockType,
    inputs: [],
    outputs: [],
  };
}

function getSocketBindingFieldId(
  node: FlowNode | null | undefined,
  direction: 'input' | 'output',
  socketId: string
) {
  const bindings = direction === 'input' ? node?.inputs : node?.outputs;
  return bindings?.find((binding) => binding.socketId === socketId)?.fieldId ?? null;
}

function findSingleNodeId(flow: FlowDefinition, blockType: string) {
  const matchingNodeIds = flow.nodes
    .filter((node) => node.blockType === blockType)
    .map((node) => node.id);
  return matchingNodeIds.length === 1 ? matchingNodeIds[0] : null;
}

function connectIfBothSidesUnbound(
  flow: FlowDefinition,
  args: {
    outputNodeId: string | null;
    outputSocketId: string;
    inputNodeId: string | null;
    inputSocketId: string;
    fieldType: FlowFieldDefinition['type'];
    fieldLabel: string;
  }
) {
  const outputNode = args.outputNodeId
    ? flow.nodes.find((node) => node.id === args.outputNodeId) ?? null
    : null;
  const inputNode = args.inputNodeId
    ? flow.nodes.find((node) => node.id === args.inputNodeId) ?? null
    : null;
  if (!outputNode || !inputNode) return flow;
  if (getSocketBindingFieldId(outputNode, 'output', args.outputSocketId)) return flow;
  if (getSocketBindingFieldId(inputNode, 'input', args.inputSocketId)) return flow;
  return connectFlowSockets(flow, {
    outputNodeId: outputNode.id,
    outputSocketId: args.outputSocketId,
    inputNodeId: inputNode.id,
    inputSocketId: args.inputSocketId,
    fieldType: args.fieldType,
    fieldLabel: args.fieldLabel,
  });
}

export function autoWireCustomiserChain(flow: FlowDefinition) {
  const selectionCollectorNodeId = findSingleNodeId(flow, 'selection-collector');
  const finishPickerNodeId = findSingleNodeId(flow, 'customiser-finish-picker');
  const colourFilterNodeId = findSingleNodeId(flow, 'customiser-colour-filter');
  const combinationResultsNodeId = findSingleNodeId(flow, 'customiser-combination-results');
  const viewerNodeId = findSingleNodeId(flow, 'customiser-viewer');
  const buildItemNodeId = findSingleNodeId(flow, 'build-item');
  const addToListNodeId = findSingleNodeId(flow, 'add-to-list');
  const removeFromListNodeId = findSingleNodeId(flow, 'remove-from-list');
  const clearListNodeId = findSingleNodeId(flow, 'clear-list');
  const requestFormNodeId = findSingleNodeId(flow, 'request-form');

  let nextFlow = flow;

  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: selectionCollectorNodeId,
    outputSocketId: 'selection',
    inputNodeId: finishPickerNodeId,
    inputSocketId: 'selection',
    fieldType: 'context.selection',
    fieldLabel: 'Selected Product',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: selectionCollectorNodeId,
    outputSocketId: 'product',
    inputNodeId: finishPickerNodeId,
    inputSocketId: 'product',
    fieldType: 'entity.product',
    fieldLabel: 'Selected Product',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: finishPickerNodeId,
    outputSocketId: 'updatedSelection',
    inputNodeId: colourFilterNodeId,
    inputSocketId: 'selection',
    fieldType: 'context.selection',
    fieldLabel: 'Selected Choice',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: finishPickerNodeId,
    outputSocketId: 'finish',
    inputNodeId: colourFilterNodeId,
    inputSocketId: 'finish',
    fieldType: 'scalar.string',
    fieldLabel: 'Selected Finish',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: finishPickerNodeId,
    outputSocketId: 'throughProduct',
    inputNodeId: colourFilterNodeId,
    inputSocketId: 'product',
    fieldType: 'entity.product',
    fieldLabel: 'Selected Product',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: colourFilterNodeId,
    outputSocketId: 'updatedSelection',
    inputNodeId: combinationResultsNodeId,
    inputSocketId: 'selection',
    fieldType: 'context.selection',
    fieldLabel: 'Selected Choice',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: colourFilterNodeId,
    outputSocketId: 'colours',
    inputNodeId: combinationResultsNodeId,
    inputSocketId: 'colours',
    fieldType: 'list.string',
    fieldLabel: 'Chosen Colours',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: colourFilterNodeId,
    outputSocketId: 'throughProduct',
    inputNodeId: combinationResultsNodeId,
    inputSocketId: 'product',
    fieldType: 'entity.product',
    fieldLabel: 'Selected Product',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: colourFilterNodeId,
    outputSocketId: 'throughFinish',
    inputNodeId: combinationResultsNodeId,
    inputSocketId: 'finish',
    fieldType: 'scalar.string',
    fieldLabel: 'Selected Finish',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: combinationResultsNodeId,
    outputSocketId: 'updatedSelection',
    inputNodeId: viewerNodeId,
    inputSocketId: 'selection',
    fieldType: 'context.selection',
    fieldLabel: 'Resolved Selection',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: combinationResultsNodeId,
    outputSocketId: 'combination',
    inputNodeId: viewerNodeId,
    inputSocketId: 'combination',
    fieldType: 'entity.combination',
    fieldLabel: 'Selected Combination',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: combinationResultsNodeId,
    outputSocketId: 'throughProduct',
    inputNodeId: viewerNodeId,
    inputSocketId: 'product',
    fieldType: 'entity.product',
    fieldLabel: 'Product to Show',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: combinationResultsNodeId,
    outputSocketId: 'throughFinish',
    inputNodeId: viewerNodeId,
    inputSocketId: 'finish',
    fieldType: 'scalar.string',
    fieldLabel: 'Finish to Show',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: combinationResultsNodeId,
    outputSocketId: 'updatedSelection',
    inputNodeId: buildItemNodeId,
    inputSocketId: 'selection',
    fieldType: 'context.selection',
    fieldLabel: 'Current Request Selection',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: buildItemNodeId,
    outputSocketId: 'item',
    inputNodeId: addToListNodeId,
    inputSocketId: 'item',
    fieldType: 'context.item',
    fieldLabel: 'Current Request Item',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: buildItemNodeId,
    outputSocketId: 'item',
    inputNodeId: removeFromListNodeId,
    inputSocketId: 'item',
    fieldType: 'context.item',
    fieldLabel: 'Current Request Item',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: addToListNodeId,
    outputSocketId: 'updatedList',
    inputNodeId: requestFormNodeId,
    inputSocketId: 'requestItems',
    fieldType: 'list.request-item',
    fieldLabel: 'Request Items',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: addToListNodeId,
    outputSocketId: 'updatedList',
    inputNodeId: removeFromListNodeId,
    inputSocketId: 'list',
    fieldType: 'list.request-item',
    fieldLabel: 'Request Items',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: addToListNodeId,
    outputSocketId: 'updatedList',
    inputNodeId: clearListNodeId,
    inputSocketId: 'list',
    fieldType: 'list.request-item',
    fieldLabel: 'Request Items',
  });
  nextFlow = connectIfBothSidesUnbound(nextFlow, {
    outputNodeId: requestFormNodeId,
    outputSocketId: 'removedItemId',
    inputNodeId: removeFromListNodeId,
    inputSocketId: 'removeItemId',
    fieldType: 'scalar.string',
    fieldLabel: 'Removed Item ID',
  });

  return nextFlow;
}

export function upsertCustomiserBlockFlowNode(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    blockId: string;
    blockType: CustomiserBlockNodeType;
    autoWire?: boolean;
    sections?: Array<{
      id: string;
      settings?: {
        canvas?: {
          blocks?: Array<{
            id: string;
            type: string;
          }>;
        };
      };
    }> | null;
  }
) {
  const { flows: nextFlows, flow: targetFlow } = resolveOrCreateSectionInsertionFlow(flows, {
    sectionId: args.sectionId,
    sections: args.sections,
  });

  if (!targetFlow) return nextFlows;

  const updatedFlowBase: FlowDefinition = {
    ...targetFlow,
    fields: upsertFields(
      targetFlow.fields,
      args.blockType === 'customiser-viewer'
        ? [...CUSTOMISER_FLOW_FIELDS, ...buildViewerControlFields(args.blockId)]
        : CUSTOMISER_FLOW_FIELDS
    ),
    nodes: upsertNodes(targetFlow.nodes, [buildCustomiserBlockNode(args.blockType, args.blockId)]),
  };

  const updatedFlowWithChain = args.autoWire ? autoWireCustomiserChain(updatedFlowBase) : updatedFlowBase;
  const updatedFlow: FlowDefinition = {
    ...updatedFlowWithChain,
    connections: rebuildFlowConnections(updatedFlowWithChain),
  };

  return nextFlows.map((flow) => (flow.id === updatedFlow.id ? updatedFlow : flow));
}

export function upsertCustomiserBlockFlowNodesForSections(
  flows: FlowDefinition[],
  sections: Array<{
    id: string;
    settings?: {
      canvas?: {
        blocks?: Array<{
          id: string;
          type: string;
        }>;
      };
    };
  }>
) {
  return sections.reduce((nextFlows, section) => {
    const customiserBlocks = section.settings?.canvas?.blocks?.filter((block) =>
      block.type === 'customiser-viewer' ||
      block.type === 'customiser-finish-picker' ||
      block.type === 'customiser-colour-filter' ||
      block.type === 'customiser-combination-results'
    );
    if (!customiserBlocks || customiserBlocks.length === 0) return nextFlows;
    return customiserBlocks.reduce(
      (currentFlows, block) =>
        upsertCustomiserBlockFlowNode(currentFlows, {
          sectionId: section.id,
          blockId: block.id,
          blockType: block.type as CustomiserBlockNodeType,
          sections,
        }),
      nextFlows
    );
  }, flows);
}
